import { type RefObject, useRef } from 'react';
import { type TrackAdformImpressionsRequestBody, TrackAdformImpressionsUrl } from 'autogen/swagger/all';
import { useIntersectionEffect } from 'hooks/globals/useIntersectionEffect';

type trackingUrlsArray = TrackAdformImpressionsRequestBody['trackingUrls'];
declare global {
    interface Window {
        Adform?: {
            _uid: string;
        };
    }
}

function postImpressionTracking(data: trackingUrlsArray) {
    if (data.length === 0) return;
    const uid = window.Adform?._uid ?? '';
    const body: TrackAdformImpressionsRequestBody = { trackingUrls: data, uid };

    fetch(TrackAdformImpressionsUrl, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body),
        credentials: 'same-origin',
        keepalive: true
    }).catch((error) => {
        throw error;
    });
}

let bulkArray = [] as trackingUrlsArray;
function postAndReset() {
    postImpressionTracking(bulkArray);
    bulkArray = [];
    window?.removeEventListener('beforeunload', postAndReset);
}

let timer;
const waitTime = 1000;
const maxBulkSize = 20;
function handlePostAndReset() {
    clearTimeout(timer);

    if (window) {
        window.addEventListener('beforeunload', postAndReset);
    }

    if (bulkArray.length >= maxBulkSize) {
        postAndReset();
        return;
    }

    timer = setTimeout(() => {
        postAndReset();
    }, waitTime);
}

export function bulkTogether(trackingUrls: trackingUrlsArray) {
    trackingUrls.forEach((trackingUrl) => {
        bulkArray.push(trackingUrl);
    });

    handlePostAndReset();
}

export default function useAdformImpressionTracking({
    ref,
    impTrackingUrls
}: {
    ref: RefObject<Element>;
    impTrackingUrls?: trackingUrlsArray;
}) {
    const hasTrackedRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const threshold = 0.5;

    useIntersectionEffect(
        (entries, observer) => {
            const entry = entries[0];

            if (!impTrackingUrls?.length || hasTrackedRef.current) {
                observer.disconnect();
                return;
            }

            if (entry?.isIntersecting && !hasTrackedRef.current) {
                hasTrackedRef.current = true;
                timerRef.current = setTimeout(() => {
                    bulkTogether(impTrackingUrls);
                    observer.disconnect();
                }, 1000);
            }
        },
        {
            ref,
            observerInit: {
                threshold,
                rootMargin: '0%'
            }
        },
        [ref, impTrackingUrls, threshold]
    );
}
